import { Button, message, Spin, Timeline } from 'antd';
import { useState, useCallback, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { JustRightHeightModal, TimelineWrapper } from 'components/styled/scoped';
import { useRequest } from 'ahooks';
import { resolve, storeHomeV2Client } from 'services/api';
import { css } from '@emotion/css';
import dayjs from 'lib/dayjs';
import { utcRender, getLocalTimeZone } from 'utils/timeZone';
import Empty from 'components/Empty';

type ChangeLogReturn = {
  timeZone: number;
  stateChangeTimeLine: {
    state: string;
    createTimestamp: number;
    clientType: 'WEB';
  }[];
};

const getUserStatueChangeLog = (userId: string) => {
  return resolve<ChangeLogReturn>(
    storeHomeV2Client.get('/admin/get_user_statue_change_log', {
      params: {
        userId,
      },
    }),
  );
};

type SwitchLineDetailProps = {
  visible: boolean;
  onClose: () => void;
  userId: string;
};

const SwitchLineDetail = (props: SwitchLineDetailProps) => {
  const { t } = useTranslate();
  const { visible, onClose, userId } = props;
  const { data, loading } = useRequest(() => getUserStatueChangeLog(userId), {
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });
  const localTimeZoneRef = useRef(getLocalTimeZone());

  const stateMap: any = {
    RECOVER: t('status.online'),
    LOGOUT: t('hangup_offline'),
    LOGIN: t('hangup_online'),
    HANG: t('status.hangup'),
  };

  return (
    <JustRightHeightModal
      centered
      visible={visible}
      onCancel={onClose}
      footer={null}
      title={t('hangup_statue_log')}
      width={427}
      className={css`
        .ant-modal-body {
          padding: 16px 16px 30px 16px;
          margin: 0;
        }
      `}
    >
      {loading ? (
        <div className="min-h-[180px] flex items-center justify-center">
          <Spin />
        </div>
      ) : data?.stateChangeTimeLine?.length ? (
        <div>
          <TimelineWrapper>
            {data?.stateChangeTimeLine.map((item) => (
              <Timeline.Item
                key={item.createTimestamp}
                color={item.state === 'ONLINE' ? 'green' : 'gray'}
              >
                <div className="text-[var(--fett-text-primary)] leading-[18px]">
                  {stateMap[item.state] || `${t('status.hangup')}-${item.state}`}
                </div>
                <div className="text-[var(--fett-text-tertiary)] leading-[18px] mt-[6px] text-[12px]">
                  {dayjs(item.createTimestamp)
                    .utcOffset(data?.timeZone)
                    .format('YYYY-MM-DD HH:mm:ss')}
                  {localTimeZoneRef.current !== data?.timeZone
                    ? `(${utcRender(data?.timeZone)})`
                    : ''}
                </div>
              </Timeline.Item>
            ))}
          </TimelineWrapper>
        </div>
      ) : (
        <Empty />
      )}
    </JustRightHeightModal>
  );
};

const useSwitchLineDetail = () => {
  const [visible, setVisible] = useState(false);
  const userIdRef = useRef<string>();
  const open = useCallback((userId: string) => {
    userIdRef.current = userId;
    setVisible(true);
  }, []);
  const close = useCallback(() => setVisible(false), []);
  const instance = visible && (
    <SwitchLineDetail visible={visible} onClose={close} userId={userIdRef.current!} />
  );
  return [open, instance] as const;
};

export const SwitchLineDetailButton = (props: { userId: string }) => {
  const { t } = useTranslate();
  const [open, instance] = useSwitchLineDetail();
  const { userId } = props;

  return (
    <>
      <Button onClick={() => open(userId)} type="link">
        {t('todo.明细')}
      </Button>
      {instance}
    </>
  );
};
